import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Eye, DollarSign, Filter, RefreshCw, ChevronLeft, ChevronRight, AlertCircle, Factory, Bot, CalendarCheck, CalendarX } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { Spinner } from '../../design-system';
import { CreatePricingModal, UpdatePricingModal, PricingDetailsModal } from './ModelPricingModals';

interface PricingTierDisplay {
  min_input_tokens: number;
  max_input_tokens?: number | null;
  input_price: string;
  output_price: string;
  cache_read_price?: string;
  cache_write_price?: string;
}

interface ModelPricing {
  id: string;
  provider: string;
  model: string;
  input_price: string;
  output_price: string;
  cache_read_price?: string;
  cache_write_price?: string;
  pricing_strategy: 'flat_per_mtok' | 'tiered_per_request';
  tiers?: PricingTierDisplay[] | null;
  is_active: boolean;
  effective_date: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  notes?: string;
}

interface ModelPricingListResponse {
  items: ModelPricing[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface PricingTierForm {
  min_input_tokens: number;
  max_input_tokens?: number | null;
  input_price: number;
  output_price: number;
  cache_read_price?: number;
  cache_write_price?: number;
}

interface ModelPricingCreateRequest {
  provider: string;
  model: string;
  input_price: number;
  output_price: number;
  cache_read_price?: number;
  cache_write_price?: number;
  pricing_strategy: 'flat_per_mtok' | 'tiered_per_request';
  tiers?: PricingTierForm[];
  is_active: boolean;
  notes?: string;
}

const ModelPricingSection: React.FC = () => {
  const authenticatedApi = useAuthenticatedApi();
  const [pricingData, setPricingData] = useState<ModelPricingListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Filter state
  const [filters, setFilters] = useState({
    provider: '',
    model: '',
    is_active: undefined as boolean | undefined
  });
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<ModelPricing | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<ModelPricingCreateRequest>({
    provider: '',
    model: '',
    input_price: 0,
    output_price: 0,
    cache_read_price: undefined,
    cache_write_price: undefined,
    pricing_strategy: 'flat_per_mtok',
    tiers: [],
    is_active: true,
    notes: ''
  });
  
  // Dropdown data
  const [providers, setProviders] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  // Auto-clear modal error after 5 seconds
  useEffect(() => {
    if (modalError) {
      const timeout = setTimeout(() => {
        setModalError(null);
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [modalError]);

  const fetchPricingData = useCallback(async () => {
    if (!authenticatedApi) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString()
      });
      
      if (filters.provider) params.append('provider', filters.provider);
      if (filters.model) params.append('model', filters.model);
      if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
      
      const response = await authenticatedApi.get(`${apiEndpoints.admin.modelPricing}?${params}`);
      setPricingData(response);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch model pricing data');
    } finally {
      setLoading(false);
    }
  }, [authenticatedApi, currentPage, pageSize, filters]);

  const fetchProviders = useCallback(async () => {
    if (!authenticatedApi) return;
    
    try {
      setLoadingProviders(true);
      const response = await authenticatedApi.get(apiEndpoints.admin.modelPricingProviders);
      setProviders(response);
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    } finally {
      setLoadingProviders(false);
    }
  }, [authenticatedApi]);

  const fetchModels = useCallback(async (provider: string) => {
    if (!authenticatedApi || !provider) {
      setModels([]);
      return;
    }
    
    try {
      setLoadingModels(true);
      const response = await authenticatedApi.get(apiEndpoints.admin.modelPricingModelsByProvider(provider));
      setModels(response);
    } catch (err) {
      console.error('Failed to fetch models:', err);
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  }, [authenticatedApi]);

  useEffect(() => {
    fetchPricingData();
    fetchProviders();
  }, [fetchPricingData, fetchProviders]);

  // Fetch models when provider filter changes
  useEffect(() => {
    if (filters.provider) {
      fetchModels(filters.provider);
    } else {
      setModels([]);
    }
  }, [filters.provider, fetchModels]);

  const handleCreate = async () => {
    if (!authenticatedApi) return;
    
    try {
      await authenticatedApi.post(apiEndpoints.admin.modelPricing, formData);
      setShowCreateModal(false);
      resetForm();
      fetchPricingData();
    } catch (err: any) {
      setModalError(err.message || 'Failed to create model pricing');
    }
  };

  const handleUpdate = async () => {
    if (!authenticatedApi || !selectedPricing) return;
    
    try {
      const updateData = {
        input_price: formData.input_price,
        output_price: formData.output_price,
        cache_read_price: formData.cache_read_price,
        cache_write_price: formData.cache_write_price,
        pricing_strategy: formData.pricing_strategy,
        tiers: formData.tiers,
        is_active: formData.is_active,
        notes: formData.notes
      };
      
      await authenticatedApi.put(apiEndpoints.admin.modelPricingById(selectedPricing.id), updateData);
      setShowUpdateModal(false);
      setSelectedPricing(null);
      resetForm();
      fetchPricingData();
    } catch (err: any) {
      setModalError(err.message || 'Failed to update model pricing');
    }
  };

  const resetForm = () => {
    setFormData({
      provider: '',
      model: '',
      input_price: 0,
      output_price: 0,
      cache_read_price: undefined,
      cache_write_price: undefined,
      pricing_strategy: 'flat_per_mtok',
      tiers: [],
      is_active: true,
      notes: ''
    });
  };

  const openCreateModal = () => {
    resetForm();
    setModalError(null);
    setShowCreateModal(true);
  };

  const openUpdateModal = (pricing: ModelPricing) => {
    setSelectedPricing(pricing);
    setFormData({
      provider: pricing.provider,
      model: pricing.model,
      input_price: parseFloat(pricing.input_price),
      output_price: parseFloat(pricing.output_price),
      cache_read_price: pricing.cache_read_price ? parseFloat(pricing.cache_read_price) : undefined,
      cache_write_price: pricing.cache_write_price ? parseFloat(pricing.cache_write_price) : undefined,
      pricing_strategy: pricing.pricing_strategy || 'flat_per_mtok',
      tiers: (pricing.tiers || undefined)?.map(t => ({
        min_input_tokens: t.min_input_tokens,
        max_input_tokens: t.max_input_tokens ?? null,
        input_price: parseFloat(t.input_price),
        output_price: parseFloat(t.output_price),
        cache_read_price: t.cache_read_price ? parseFloat(t.cache_read_price) : undefined,
        cache_write_price: t.cache_write_price ? parseFloat(t.cache_write_price) : undefined,
      })),
      is_active: pricing.is_active,
      notes: pricing.notes || ''
    });
    setModalError(null);
    setShowUpdateModal(true);
  };

  const openDetailsModal = (pricing: ModelPricing) => {
    setSelectedPricing(pricing);
    setShowDetailsModal(true);
  };

  const formatPrice = (price: string | undefined) => {
    if (price === undefined || price === null) return 'N/A';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return 'N/A';
    return `$${numPrice.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      // Reset model when provider changes
      if (key === 'provider') {
        newFilters.model = '';
      }
      return newFilters;
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      provider: '',
      model: '',
      is_active: undefined
    });
    setCurrentPage(1);
  };

  return (
    <div className="min-w-0 overflow-x-hidden">
      <div className="p-4 sm:p-6">
      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-ui-text-primary mb-2">Model Pricing Management</h2>
          <p className="text-sm text-ui-text-secondary">
            Manage LLM model pricing configurations with historical tracking
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <button
            onClick={openCreateModal}
            className="flex items-center justify-center gap-2 bg-brand-primary text-black px-4 py-2 rounded-lg hover:bg-brand-primary/90 transition-colors whitespace-nowrap"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Add New Pricing</span>
            <span className="sm:hidden">Add Pricing</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={16} className="text-ui-text-tertiary" />
          <span className="text-sm font-medium text-ui-text-secondary">Filters</span>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex flex-col sm:flex-row gap-3 flex-1 min-w-0">
            <select
              value={filters.provider}
              onChange={(e) => handleFilterChange('provider', e.target.value)}
              disabled={loadingProviders}
              className="flex-1 min-w-0 px-3 py-2 border border-ui-border-primary rounded-lg text-sm bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">{loadingProviders ? 'Loading providers...' : 'All Providers'}</option>
              {providers.map((provider) => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>
            
            <select
              value={filters.model}
              onChange={(e) => handleFilterChange('model', e.target.value)}
              disabled={loadingModels || !filters.provider}
              className="flex-1 min-w-0 px-3 py-2 border border-ui-border-primary rounded-lg text-sm bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">
                {!filters.provider ? 'Select provider first' : loadingModels ? 'Loading models...' : 'All Models'}
              </option>
              {models.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
            
            <select
              value={filters.is_active === undefined ? '' : filters.is_active.toString()}
              onChange={(e) => handleFilterChange('is_active', e.target.value === '' ? undefined : e.target.value === 'true')}
              className="sm:w-32 px-3 py-2 border border-ui-border-primary rounded-lg text-sm bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-brand-primary border border-brand-primary rounded-lg hover:bg-brand-primary/10 dark:hover:bg-brand-primary/20 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={fetchPricingData}
              disabled={loading}
              className="px-4 py-2 bg-brand-primary text-black hover:bg-brand-primary/90 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Table Container - Fixed responsive issues */}
      <div className="relative overflow-hidden">
        <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl overflow-hidden">
        {/* Desktop Table - Show only on screens 1400px and above */}
        <div className="hidden min-[1400px]:block">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed">
              <thead className="bg-ui-background-tertiary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Provider/Model
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Input
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Output
                  </th>
                  <th className="w-36 px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Cache
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="w-48 px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Date
                  </th>
                  <th className="w-24 px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="relative bg-ui-background-secondary divide-y divide-ui-border-primary">
                {/* Loading Overlay for Table Body */}
                {loading && (
                  <tr>
                    <td colSpan={7} className="relative h-32">
                      <div className="absolute inset-0 bg-white/75 dark:bg-neutral-700/75 backdrop-blur-sm flex items-center justify-center">
                        <div className="flex items-center gap-3 px-4 py-2 bg-ui-background-secondary rounded-lg shadow-lg border border-ui-border-primary">
                          <Spinner size={20} />
                          <span className="text-sm font-medium text-ui-text-secondary">Loading...</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                
                {/* Data Rows or Empty State */}
                {!loading && pricingData && pricingData.items.length > 0 ? (
                  pricingData.items.map((pricing) => (
                    <tr key={pricing.id} className="hover:bg-ui-background-tertiary">
                      <td className="px-4 py-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Factory size={14} className="text-ui-text-tertiary flex-shrink-0" />
                            <span className="text-sm font-medium text-ui-text-primary truncate">
                              {pricing.provider}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Bot size={14} className="text-ui-text-tertiary flex-shrink-0" />
                            <span className="text-sm text-ui-text-secondary truncate">
                              {pricing.model}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-sm text-ui-text-primary font-medium">
                          <DollarSign size={14} className="text-green-500 dark:text-green-400 flex-shrink-0" />
                          <span>{formatPrice(pricing.input_price)}</span>
                        </div>
                        <div className="text-xs text-ui-text-tertiary">per 1M</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-sm text-ui-text-primary font-medium">
                          <DollarSign size={14} className="text-blue-500 dark:text-blue-400 flex-shrink-0" />
                          <span>{formatPrice(pricing.output_price)}</span>
                        </div>
                        <div className="text-xs text-ui-text-tertiary">per 1M</div>
                      </td>
                      <td className="w-36 px-4 py-4">
                        <div className="space-y-0.5">
                          <div className="text-xs">
                            <span className="text-ui-text-tertiary">R: </span>
                            <span className="text-ui-text-primary">
                              {formatPrice(pricing.cache_read_price)}
                            </span>
                          </div>
                          <div className="text-xs">
                            <span className="text-ui-text-tertiary">W: </span>
                            <span className="text-ui-text-primary">
                              {formatPrice(pricing.cache_write_price)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                          pricing.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          {pricing.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="w-48 px-4 py-4">
                        {/* Effective Date Badge */}
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-ui-text-primary">
                           <CalendarCheck
                             size={16}
                             className={pricing.is_active ? 'text-green-600 flex-shrink-0' : 'text-ui-text-tertiary flex-shrink-0'}
                           />
                           Start: {formatDate(pricing.effective_date)}
                         </div>
                        {/* End Date Badge */}
                        {pricing.end_date && (
                          <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-ui-text-primary">
                             <CalendarX size={16} className="text-red-600 flex-shrink-0" />
                             End: {formatDate(pricing.end_date)}
                           </div>
                        )}
                      </td>
                      <td className="w-24 px-4 py-4">
                        <div className="flex gap-3">
                          <button
                            onClick={() => openDetailsModal(pricing)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => openUpdateModal(pricing)}
                            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 transition-colors"
                            title="Update Pricing"
                          >
                            <Edit size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : !loading && (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <DollarSign className="mx-auto mb-4 text-ui-text-tertiary" size={48} />
                      <p className="text-ui-text-secondary mb-6">No model pricing entries found</p>
                      <button
                        onClick={openCreateModal}
                        className="px-6 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors flex items-center gap-2 mx-auto"
                      >
                        <Plus size={16} />
                        Add First Pricing Entry
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card Layout - Show below 1400px screens */}
        <div className="min-[1400px]:hidden relative min-h-[200px]">
          {/* Loading Overlay for Mobile */}
          {loading && (
            <div className="absolute inset-0 bg-white/75 dark:bg-neutral-700/75 backdrop-blur-sm flex items-center justify-center z-10 rounded-xl">
              <div className="flex items-center gap-3 px-4 py-2 bg-ui-background-secondary rounded-lg shadow-lg border border-ui-border-primary">
                <Spinner size={20} />
                <span className="text-sm font-medium text-ui-text-secondary">Loading...</span>
              </div>
            </div>
          )}
          
          {/* Mobile Cards or Empty State */}
          {!loading && pricingData && pricingData.items.length > 0 ? (
            <div className="divide-y divide-ui-border-primary">
              {pricingData.items.map((pricing) => (
                <div key={pricing.id} className="p-4 hover:bg-ui-background-tertiary">
                  {/* Provider/Model */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Factory size={16} className="text-ui-text-tertiary flex-shrink-0" />
                      <span className="text-sm font-medium text-ui-text-primary">
                        {pricing.provider}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Bot size={16} className="text-ui-text-tertiary flex-shrink-0" />
                      <span className="text-sm text-ui-text-secondary">
                        {pricing.model}
                      </span>
                    </div>
                  </div>

                  {/* Pricing Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <div className="text-xs text-ui-text-tertiary mb-1">Input Price</div>
                      <div className="flex items-center gap-1">
                        <DollarSign size={12} className="text-green-500 dark:text-green-400 flex-shrink-0" />
                        <span className="text-sm text-ui-text-primary">
                          {formatPrice(pricing.input_price)}
                        </span>
                      </div>
                      <div className="text-xs text-ui-text-tertiary">per 1M tokens</div>
                    </div>
                    <div>
                      <div className="text-xs text-ui-text-tertiary mb-1">Output Price</div>
                      <div className="flex items-center gap-1">
                        <DollarSign size={12} className="text-blue-500 dark:text-blue-400 flex-shrink-0" />
                        <span className="text-sm text-ui-text-primary">
                          {formatPrice(pricing.output_price)}
                        </span>
                      </div>
                      <div className="text-xs text-ui-text-tertiary">per 1M tokens</div>
                    </div>
                  </div>

                  {/* Cache Prices */}
                  <div className="mb-3">
                    <div className="text-xs text-ui-text-tertiary mb-1">Cache Prices</div>
                    <div className="flex gap-4">
                      <div className="text-xs">
                        <span className="text-ui-text-tertiary">Read: </span>
                        <span className="text-ui-text-primary">
                          {formatPrice(pricing.cache_read_price)}
                        </span>
                      </div>
                      <div className="text-xs">
                        <span className="text-ui-text-tertiary">Write: </span>
                        <span className="text-ui-text-primary">
                          {formatPrice(pricing.cache_write_price)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Status and Date */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      pricing.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {pricing.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <div className="flex flex-col items-end text-xs">
                      <div className="inline-flex items-center gap-1">
                        <CalendarCheck size={12} className={pricing.is_active ? 'text-green-600' : 'text-ui-text-tertiary'} />
                        <span className="text-ui-text-secondary">{formatDate(pricing.effective_date)}</span>
                      </div>
                      {pricing.end_date && (
                        <div className="inline-flex items-center gap-1 mt-0.5">
                          <CalendarX size={12} className="text-red-600" />
                          <span className="text-ui-text-secondary">{formatDate(pricing.end_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => openDetailsModal(pricing)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs bg-ui-background-tertiary dark:bg-neutral-600 text-ui-text-secondary rounded-md hover:bg-ui-background-tertiary transition-colors"
                    >
                      <Eye size={12} />
                      View
                    </button>
                    <button
                      onClick={() => openUpdateModal(pricing)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs bg-brand-primary text-black rounded-md hover:bg-brand-primary/90 transition-colors"
                    >
                      <Edit size={12} />
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : !loading && (
            <div className="p-8 text-center">
              <DollarSign className="mx-auto mb-4 text-ui-text-tertiary" size={48} />
              <p className="text-ui-text-secondary mb-6">No model pricing entries found</p>
              <button
                onClick={openCreateModal}
                className="px-6 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primary/90 transition-colors flex items-center gap-2 mx-auto"
              >
                <Plus size={16} />
                Add First Pricing Entry
              </button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pricingData && pricingData.total_pages > 1 && (
          <div className="bg-ui-background-tertiary px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-ui-border-primary">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <span className="text-sm text-ui-text-secondary text-center sm:text-left">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, pricingData.total_count)} of {pricingData.total_count} entries
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-ui-border-primary rounded-lg text-sm bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={!pricingData.has_prev}
                className="p-2 text-ui-text-tertiary hover:text-ui-text-primary dark:text-ui-text-tertiary dark:hover:text-ui-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              
              <span className="text-sm text-ui-text-secondary">
                Page {currentPage} of {pricingData.total_pages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(pricingData.total_pages, prev + 1))}
                disabled={!pricingData.has_next}
                className="p-2 text-ui-text-tertiary hover:text-ui-text-primary dark:text-ui-text-tertiary dark:hover:text-ui-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Modals */}
      <CreatePricingModal
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setModalError(null);
        }}
        onSubmit={handleCreate}
        formData={formData}
        setFormData={setFormData}
        providers={providers}
        error={modalError}
      />
      
      <UpdatePricingModal
        show={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedPricing(null);
          setModalError(null);
        }}
        onSubmit={handleUpdate}
        formData={formData}
        setFormData={setFormData}
        selectedPricing={selectedPricing}
        error={modalError}
      />
      
      <PricingDetailsModal
        show={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedPricing(null);
        }}
        pricing={selectedPricing}
      />
      </div>
    </div>
  );
};

export default ModelPricingSection;